import { useEffect, useState } from 'react';

/** Cap how much we pull into the DOM so a huge file can't lock up the tab. */
const MAX_CHARS = 500_000;
const MAX_ROWS = 1_000;

interface TextViewerProps {
	url: string;
	name: string;
	mimeType: string;
}

type Parsed =
	| { kind: 'table'; headers: string[]; rows: string[][]; truncatedRows: boolean }
	| { kind: 'text'; body: string };

function isDelimited(name: string, mimeType: string): ',' | '\t' | null {
	const ext = name.split('.').pop()?.toLowerCase();
	if (ext === 'csv' || mimeType === 'text/csv' || mimeType === 'application/csv') return ',';
	if (ext === 'tsv') return '\t';
	return null;
}

/** Minimal RFC-4180-ish parser: handles quoted fields, escaped quotes, CRLF. */
function parseDelimited(text: string, delimiter: string): string[][] {
	const rows: string[][] = [];
	let field = '';
	let row: string[] = [];
	let inQuotes = false;

	for (let i = 0; i < text.length; i++) {
		const char = text[i];

		if (inQuotes) {
			if (char === '"') {
				if (text[i + 1] === '"') {
					field += '"';
					i++;
				} else {
					inQuotes = false;
				}
			} else {
				field += char;
			}
			continue;
		}

		if (char === '"') {
			inQuotes = true;
		} else if (char === delimiter) {
			row.push(field);
			field = '';
		} else if (char === '\n') {
			row.push(field);
			rows.push(row);
			row = [];
			field = '';
		} else if (char !== '\r') {
			field += char;
		}
	}

	// Flush trailing field/row (unless the file ended on a clean newline).
	if (field !== '' || row.length > 0) {
		row.push(field);
		rows.push(row);
	}

	return rows;
}

export function TextViewer({ url, name, mimeType }: TextViewerProps) {
	const [parsed, setParsed] = useState<Parsed | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [truncatedChars, setTruncatedChars] = useState(false);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(false);
		setParsed(null);
		setTruncatedChars(false);

		fetch(url)
			.then((res) => {
				if (!res.ok) throw new Error(`HTTP ${res.status}`);
				return res.text();
			})
			.then((raw) => {
				if (cancelled) return;
				const clipped = raw.length > MAX_CHARS ? raw.slice(0, MAX_CHARS) : raw;
				setTruncatedChars(raw.length > MAX_CHARS);

				const delimiter = isDelimited(name, mimeType);
				if (delimiter) {
					const allRows = parseDelimited(clipped, delimiter).filter(
						(r) => r.length > 1 || r[0] !== ''
					);
					const [headers = [], ...dataRows] = allRows;
					const truncatedRows = dataRows.length > MAX_ROWS;
					setParsed({
						kind: 'table',
						headers,
						rows: truncatedRows ? dataRows.slice(0, MAX_ROWS) : dataRows,
						truncatedRows,
					});
				} else {
					setParsed({ kind: 'text', body: clipped });
				}
			})
			.catch(() => {
				if (!cancelled) setError(true);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [url, name, mimeType]);

	if (loading) {
		return (
			<div className="flex h-full items-center justify-center bg-surface-sunken text-base text-text-4">
				Loading preview…
			</div>
		);
	}

	if (error || !parsed) {
		return (
			<div className="flex h-full items-center justify-center bg-surface-sunken text-base text-text-4">
				Couldn&apos;t load this file.
			</div>
		);
	}

	if (parsed.kind === 'table') {
		return (
			<div className="flex h-full flex-col bg-surface-sunken">
				<div className="min-h-0 flex-1 overflow-auto">
					<table className="w-full border-collapse text-sm">
						{parsed.headers.length > 0 && (
							<thead className="sticky top-0 z-10">
								<tr>
									{parsed.headers.map((cell, i) => (
										<th
											key={i}
											className="whitespace-nowrap border-b border-line-1 bg-surface px-3 py-2 text-left font-medium text-text-2"
										>
											{cell}
										</th>
									))}
								</tr>
							</thead>
						)}
						<tbody>
							{parsed.rows.map((row, r) => (
								<tr key={r} className="hover:bg-surface">
									{parsed.headers.map((_, c) => (
										<td
											key={c}
											className="whitespace-nowrap border-b border-line-1 px-3 py-1.5 text-text-1"
										>
											{row[c] ?? ''}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
				{(parsed.truncatedRows || truncatedChars) && (
					<div className="shrink-0 border-t border-line-1 bg-surface px-3 py-1.5 text-xs text-text-4">
						Showing first {parsed.rows.length.toLocaleString()} rows — download to see
						the full file.
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col bg-surface-sunken">
			<div className="min-h-0 flex-1 overflow-auto">
				<pre className="whitespace-pre px-4 py-3 font-mono text-sm leading-relaxed text-text-1">
					{parsed.body}
				</pre>
			</div>
			{truncatedChars && (
				<div className="shrink-0 border-t border-line-1 bg-surface px-3 py-1.5 text-xs text-text-4">
					Preview truncated — download to see the full file.
				</div>
			)}
		</div>
	);
}
